/**
 * Purge du stockage hors-ligne (IndexedDB/Dexie) — point d'entrée UNIQUE.
 *
 * À appeler au logout, au changement de tenant et à la récupération de session
 * (mêmes points d'appel historiques que `viderCacheParcelles` : UserMenu,
 * SwitchTenantConfirmDialog, main.tsx). Sur un appareil partagé, on ne laisse
 * AUCUNE donnée d'un tenant derrière soi (règle projet #3) : ni les caches de
 * lecture (parcelles, lots dégorgeables, catalogue, grille fermentations), ni
 * les files d'attente (payloads métier : poids, dosages, lots, tickets caveau).
 *
 * Une sortie volontaire est refusée tant qu'une opération attend. Une session
 * révoquée copie d'abord ces opérations dans la base de quarantaine séparée.
 * Ce helper reste volontairement destructif : ses callers doivent donc avoir
 * appliqué l'une de ces deux gardes avant de l'appeler.
 */

import { offlineDb } from "./db.js";

/**
 * Compte les opérations qui n'ont pas encore une confirmation serveur.
 * La détection est dérivée de l'index `statut` des stores Dexie : une future
 * file conforme à `EntreeFileSync` sera automatiquement incluse.
 */
export async function compterOperationsHorsLigneNonSynchronisees(): Promise<number> {
  const files = offlineDb.tables.filter((table) =>
    table.schema.indexes.some((index) => index.name === "statut"),
  );
  const comptes = await Promise.all(
    files.map((table) => table.filter((entree) => entree.statut !== "envoye").count()),
  );
  return comptes.reduce((total, compte) => total + compte, 0);
}

/**
 * Vide TOUTE la file des pesées (tous tenants). Symétrique de
 * `viderCacheParcelles` : purge intégrale, pas seulement l'entrée courante.
 */
export async function viderFilePesees(): Promise<void> {
  await offlineDb.peseesQueue.clear();
}

/** Vide TOUTE la file des tickets extérieurs (tous tenants). */
export async function viderFileTicketsExterieurs(): Promise<void> {
  await offlineDb.ticketsExterieursQueue.clear();
}

/** Vide TOUTE la file des dégorgements (tous tenants) — même politique. */
export async function viderFileDegorgements(): Promise<void> {
  await offlineDb.degorgementsQueue.clear();
}

/**
 * Vide la file des ventes caveau + le cache catalogue + le panier persisté (Lot
 * G), tous tenants. Les gardes de sortie de session sont responsables de ne
 * jamais appeler cette purge avec une opération non protégée.
 */
export async function viderFileVentesCaveau(): Promise<void> {
  await Promise.all([
    offlineDb.venteCaveauQueue.clear(),
    offlineDb.venteProduitsCache.clear(),
    offlineDb.panierCaveauCache.clear(),
  ]);
}

/**
 * Vide la file des relevés de fermentation (lot-i É1), tous tenants —
 * même politique que les pesées : l'isolation prime sur la rétention.
 */
export async function viderFileRelevesFermentation(): Promise<void> {
  await offlineDb.relevesFermentationQueue.clear();
}

/**
 * Purge TOUT le stockage hors-ligne. C'est CE helper que les points de sortie
 * de session doivent appeler.
 *
 * La liste des stores est DÉRIVÉE du schéma Dexie (`offlineDb.tables`), jamais
 * énumérée à la main : un futur store est purgé d'office, sans qu'on puisse
 * l'oublier ici (une omission = données d'un tenant visibles par le suivant sur
 * un appareil partagé). Invariant vérifié par `purge-stockage.test.ts`.
 */
export async function viderStockageHorsLigne(): Promise<void> {
  await Promise.all(offlineDb.tables.map((table) => table.clear()));
}
